export interface AlarmConfigResponseModel {
  config_group: string;
  catalog_keys: string[];
  priority: 'high' | 'medium' | 'low';
  show_in_portal: boolean;
  email_enabled: boolean;
  recipients: string[];
  device_override_count: number;
  /** False when `show_in_portal`, `email_enabled` and `priority` are Xemex-fixed for this config
   * group (the `sim.*` groups): they are read-only, per-device overrides are rejected with 403, and
   * only `recipients` can still be changed. Resolved backend-side from
   * `alarm_config_group_catalog`'s locked values — never re-derive it from the group key here. */
  configurable: boolean;
}

/** The alarm-config API returns lowercase priority (`high`/`medium`/`low`, matching the
 * alarm type catalog's own convention); the UI's tag-state/priority typing elsewhere is
 * capitalized (`High`/`Medium`/`Low`) — convert at the API boundary, not deeper in the UI. */
export function capitalizePriority(priority: AlarmConfigResponseModel['priority']): 'High' | 'Medium' | 'Low' {
  return ({ high: 'High', medium: 'Medium', low: 'Low' } as const)[priority];
}

export function lowercasePriority(priority: 'High' | 'Medium' | 'Low'): AlarmConfigResponseModel['priority'] {
  return ({ High: 'high', Medium: 'medium', Low: 'low' } as const)[priority];
}

export interface AlarmConfigCollectionModel {
  items: AlarmConfigResponseModel[];
}

export interface AlarmConfigUpdateModel {
  show_in_portal: boolean;
  email_enabled: boolean;
  priority: 'high' | 'medium' | 'low';
  recipients: string[];
}

export interface DeviceOverrideResponseModel {
  device_id: number;
  ldn: string;
  address: string;
  show_in_portal: boolean | null;
  email_enabled: boolean | null;
  /** The show_in_portal/email_enabled defaults this device's override (if any) is relative to —
   * resolved against the device's own owning partner, not necessarily the viewing partner (a
   * Xemex viewer's device list can span multiple partners, each with its own global config). */
  global_show_in_portal: boolean;
  global_email_enabled: boolean;
}

export interface DeviceOverrideCollectionModel {
  items: DeviceOverrideResponseModel[];
}

export interface DeviceOverrideUpdateModel {
  show_in_portal: boolean | null;
  email_enabled: boolean | null;
}

/** One config group's effective settings for a single device — the device-settings tab's
 * single-request counterpart to calling `DeviceOverrideCollectionModel` once per config group
 * and filtering client-side for one device out of the full per-partner device list. */
export interface DeviceAlarmSettingResponseModel {
  config_group: string;
  priority: 'high' | 'medium' | 'low';
  show_in_portal: boolean;
  email_enabled: boolean;
  global_show_in_portal: boolean;
  global_email_enabled: boolean;
  /** False when this config group's settings are Xemex-fixed and cannot be overridden per device. */
  configurable: boolean;
}

export interface DeviceAlarmSettingsCollectionModel {
  items: DeviceAlarmSettingResponseModel[];
}
